// Script Analysis — the ONE place that answers "what's the trade setup"
// using REAL computed numbers from A_Signal.js, timeframe-by-timeframe.
// Zero AI, zero guessing. Built because Groq's free-form chat answers to
// "current setup?"/"today's bias?" were WRONG — it was answering from its
// own reasoning, not from Jarvis's actual validated Structure/Fibonacci/
// ADX math. This module IS that math, wired into chat.
//
// Groq stays for everything that ISN'T a trade-decision question —
// explaining a concept, psychology chat, general conversation. But "is
// there a setup right now" must never come from an LLM guessing; it comes
// from here.

import { getCandles as getLiveCandles } from '../a-agents/trading/A_LivePrice';
import { run as runSignal } from '../a-agents/trading/A_Signal';
import { run as runOrderBlocks } from '../a-agents/trading/A_OrderBlocks';
import { saveTrade, getTrades, updateTradeResult, saveMarketStructure, saveOrderBlock, saveFVG, getICT } from './storage';
import { MemoryManager } from '../brain/MemoryManager';
import { NS } from '../brain/AgentRegistry';

// ── HTF confirmation: EMA(50)/EMA(200), CONFIRMED bar only (non-repainting)
// — same helper LiveMonitor.jsx uses; centralized here so both share one
// implementation instead of drifting apart.
export function ema(values, period) {
  const k = 2 / (period + 1);
  let prev = values.slice(0, period).reduce((a, b) => a + b, 0) / period;
  const out = new Array(values.length).fill(null);
  out[period - 1] = prev;
  for (let i = period; i < values.length; i++) { prev = values[i] * k + prev * (1 - k); out[i] = prev; }
  return out;
}
export function calcHtfBias(candles) {
  if (candles.length < 201) return null;
  const closes  = candles.map(c => c.close);
  const ema50   = ema(closes, 50);
  const ema200  = ema(closes, 200);
  const i = candles.length - 2; // confirmed bar — NOT the forming last one
  if (ema50[i] == null || ema200[i] == null) return null;
  const c = closes[i];
  if (c > ema200[i] && ema50[i] > ema200[i]) return 1;
  if (c < ema200[i] && ema50[i] < ema200[i]) return -1;
  return 0;
}

// Matches jarvisDNA.trading.validated_strategy.htf_confirmation.auto_map
export const HTF_MAP = { '15m': '4h', '30m': '4h', '45m': '4h', '1h': '4h', '4h': '1d' };

// Only Pine-validated timeframes — 1m/3m/5m stay gated inside LiveMonitor's
// own backtest-gate, never auto-answered here.
export const VALIDATED_TFS = ['15m', '30m', '45m', '1h', '4h'];

// ── Trade-analysis intent detector ─────────────────────────────────────
// If the user is asking "is there a setup / what's the bias / should I
// trade", this must be answered from real math, never from Groq guessing.
// Deliberately broad (Marathi+English mixed, matches how Sir actually
// types) — a false positive just means real numbers get shown instead of
// a generic chat reply, which is never wrong to do for a trading app.
const INTENT_PATTERNS = [
  /\bbias\b/i, /\bsetup\b/i, /\bsignal\b/i, /\bentry\b/i, /\btrade\b/i,
  /\bbuy\b/i, /\bsell\b/i, /\banalysis\b/i, /\banalyze\b/i,
  /आज.*(काय|कसा|कशी)/, /सध्या.*(काय|कसा)/, /सेटप/, /ट्रेड/, /कल/, /दिशा/,
];
// Exclusions — questions that mention a trade word but aren't actually
// asking "what's the setup" (e.g. the "Rate psychology" quick-prompt says
// "before I trade today" but wants a psychology check, not a signal).
const EXCLUDE_PATTERNS = [/psycholog/i, /weekly (summary|review|briefing)/i, /\bmanoshastra\b/i];

export function isTradeAnalysisIntent(text) {
  if (!text) return false;
  if (EXCLUDE_PATTERNS.some((re) => re.test(text))) return false;
  return INTENT_PATTERNS.some((re) => re.test(text));
}

// ── Run A_Signal across every validated timeframe, real data, real math ──
export async function analyzeAllTimeframes() {
  // Check yesterday's/earlier demo trades against price FIRST — so results
  // stay fresh every time this runs, not just when someone remembers to
  // check manually.
  try { await checkOpenDemoTrades(); } catch { /* non-fatal */ }

  const results = [];
  for (const tf of VALIDATED_TFS) {
    try {
      const { candles, ok: cOk } = await getLiveCandles(tf, '5d');
      if (!cOk || !candles?.length) { results.push({ tf, ok: false, error: 'candle fetch failed' }); continue; }

      const htfTF = HTF_MAP[tf] || '4h';
      const { candles: htfCandles, ok: htfOk } = await getLiveCandles(htfTF, '3mo');
      const htfBias = htfOk ? calcHtfBias(htfCandles) : null;

      const signal = await runSignal({ candles, htfBias });
      results.push({ tf, ok: signal.ok, signal });

      // Feed the REAL computed structure/fib/regime into the ICT Module +
      // Confluence Engine — those screens were manual-entry-only before
      // ("Sir types in his own Order Blocks"), which is fine for OB/FVG
      // (we don't detect those), but BOS/CHoCH/OTE-zone/HTF-bias/ADX ARE
      // things A_Signal.js already computes for real — no reason to make
      // Sir re-type what Jarvis already knows.
      if (signal.ok) {
        try { autoLogStructureToICT(tf, signal); } catch { /* non-fatal */ }
        try { saveSignalSnapshot(tf, signal); } catch { /* non-fatal */ }
        try { await autoLogOrderBlocksToICT(tf, candles, signal); } catch { /* non-fatal */ }
      }

      if (signal.ok && signal.action !== 'WAIT') {
        try { autoLogSignalAsTrade(tf, signal); } catch { /* non-fatal — journal logging must not break chat */ }
      }
    } catch (err) {
      results.push({ tf, ok: false, error: err.message });
    }
  }
  return results;
}

// ── Feed real Structure/BOS/CHoCH into the ICT Module ─────────────────────
// (Order Blocks / FVGs stay manual — A_Structure.js never detected those,
// and jarvisDNA.js explicitly marks the old "OB Fib 0.5" strategy
// unvalidated. Market Structure/BOS/CHoCH IS real, so it's auto-logged.)
function alreadyLoggedStructure(tf, signal) {
  const ms = getICT().market_structure || [];
  const recent = ms.slice(-10);
  return recent.some(m =>
    m.timeframe === tf &&
    m.direction === (signal.structure.trend === 'BULLISH' ? 'Bullish' : 'Bearish') &&
    Math.abs(m.price - signal.structure.fibExtreme) < signal.structure.fibExtreme * 0.0005
  );
}
export function autoLogStructureToICT(tf, signal) {
  if (!signal?.structure?.valid) return null;
  if (alreadyLoggedStructure(tf, signal)) return null;
  return saveMarketStructure({
    event:     'BOS',
    direction: signal.structure.trend === 'BULLISH' ? 'Bullish' : 'Bearish',
    timeframe: tf,
    price:     signal.structure.fibExtreme,
    notes:     'Auto — A_Structure.js (close-confirmed BOS, zero AI)',
  });
}

// ── Feed mechanically-detected Order Blocks / FVGs into the ICT Module ────
// 🔴 UNLIKE the structure logging above, A_OrderBlocks.js is NOT
// backtested — see the big warning at the top of that file. This only
// auto-fills the same manual-entry lists that were already there
// (Sir can still add/edit/delete by hand); it never touches A_Signal.js's
// actual BUY/SELL decision, and every entry's notes say so explicitly so
// it's never mistaken for a validated condition later.
const AUTO_OB_TAG = 'Auto — A_OrderBlocks.js (mechanical, NOT backtested — reference only)';

function alreadyLoggedOB(tf, ob) {
  const existing = getICT().order_blocks || [];
  return existing.some(o =>
    o.timeframe === tf && o.type === (ob.type === 'bullish' ? 'Bullish' : 'Bearish') &&
    Math.abs(o.top - ob.high) < ob.high * 0.0005 && Math.abs(o.bottom - ob.low) < ob.low * 0.0005
  );
}
function alreadyLoggedFVG(tf, fvg) {
  const existing = getICT().fvg || [];
  return existing.some(f =>
    f.timeframe === tf && f.type === (fvg.type === 'bullish' ? 'Bullish' : 'Bearish') &&
    Math.abs(f.top - fvg.top) < fvg.top * 0.0005 && Math.abs(f.bottom - fvg.bottom) < fvg.bottom * 0.0005
  );
}

export async function autoLogOrderBlocksToICT(tf, candles, signal) {
  if (!candles?.length || !signal?.structure) return;
  try {
    const result = await runOrderBlocks({ candles, structure: signal.structure });
    if (!result.ok) return;

    if (result.currentOrderBlock && !alreadyLoggedOB(tf, result.currentOrderBlock)) {
      saveOrderBlock({
        type: result.currentOrderBlock.type === 'bullish' ? 'Bullish' : 'Bearish',
        timeframe: tf,
        top: result.currentOrderBlock.high,
        bottom: result.currentOrderBlock.low,
        notes: AUTO_OB_TAG,
      });
    }

    // Only the most recent unfilled FVG per side — avoids flooding the
    // list every time this runs across a big historical scan.
    const latestBullFvg = result.unfilledFvgs.filter(f => f.type === 'bullish').slice(-1)[0];
    const latestBearFvg = result.unfilledFvgs.filter(f => f.type === 'bearish').slice(-1)[0];
    for (const fvg of [latestBullFvg, latestBearFvg]) {
      if (fvg && !alreadyLoggedFVG(tf, fvg)) {
        saveFVG({
          type: fvg.type === 'bullish' ? 'Bullish' : 'Bearish',
          timeframe: tf,
          top: fvg.top,
          bottom: fvg.bottom,
          notes: AUTO_OB_TAG,
        });
      }
    }
  } catch { /* non-fatal — OB/FVG auto-logging must never break the real signal flow */ }
}

// ── Stash the latest real gate-status per TF, for Confluence Engine ───────
// Confluence Engine's factor checklist used to default fib_ote/htf_bias to
// "false, needs manual confirmation" even though A_Signal.js already knows
// the real answer. This snapshot is what lets it auto-check those boxes.
const SNAPSHOT_KEY = (tf) => `signal_snapshot_${tf}`;
export function saveSignalSnapshot(tf, signal) {
  MemoryManager.write(NS.TRADING, SNAPSHOT_KEY(tf), {
    fibInZone:   !!signal.fib?.inZone,
    htfAligned:  signal.action !== 'WAIT' || signal.reason?.includes('HTF aligned') || false,
    adxTrending: !!signal.regime?.isTrending,
    bosOrChoch:  !!signal.structure?.valid,
    trend:       signal.structure?.trend || null,
    ts: Date.now(),
  });
}
export function getSignalSnapshot(tf) {
  return MemoryManager.read(NS.TRADING, SNAPSHOT_KEY(tf), null);
}
// Most-recent snapshot across all validated TFs (for a single "is anything
// currently confirming" read when the Confluence screen doesn't ask for a
// specific TF).
export function getLatestSignalSnapshot() {
  let best = null;
  VALIDATED_TFS.forEach(tf => {
    const s = getSignalSnapshot(tf);
    if (s && (!best || s.ts > best.ts)) best = { ...s, tf };
  });
  return best;
}

// Format the multi-timeframe results as a chat message — real numbers
// only, no LLM involved in composing this text.
export function formatMultiTimeframeReply(results) {
  const firm = results.filter((r) => r.ok && r.signal?.action !== 'WAIT');
  let msg = `📡 **XAUUSD — सगळ्या validated timeframes वर खरा check (Structure+Fibonacci+ADX, zero guessing):**\n\n`;

  results.forEach(({ tf, ok, signal, error }) => {
    if (!ok) { msg += `**${tf}**: ⚠️ ${error || 'analysis failed'}\n`; return; }
    if (signal.action === 'WAIT') {
      msg += `**${tf}**: ⏸ WAIT — ${signal.reason}\n`;
    } else {
      msg += `**${tf}**: ${signal.action === 'BUY' ? '🟢 BUY' : '🔴 SELL'} — Entry $${signal.entry}, SL $${signal.sl}, T1 $${signal.target} (R:R ${signal.rr})\n`;
    }
  });

  if (firm.length === 0) {
    msg += `\n**कुठल्याही timeframe वर आत्ता firm setup नाही** — सगळे gates (structure + OTE zone + ADX trending + HTF match + confirmation candle) एकाच वेळी जुळलेले नाहीत. हे खरं गणित आहे, अंदाज नाही — म्हणून सध्या "trade करू नका" हेच खरं उत्तर आहे.`;
  } else {
    msg += `\n**${firm.length} timeframe(s) वर खरा setup आहे** ⬆️ — Trade Journal मध्ये demo trade म्हणून auto-save केलंय, पुढे target/SL लागल्यावर आपोआप निकाल नोंदेल.`;
  }
  return msg;
}

// ── Auto-paper-trading: every REAL firm signal gets logged to the Trade
// Journal as a demo trade (entry/SL/T1 frozen at signal time), then future
// checks (checkOpenDemoTrades) watch price against those frozen levels and
// close it out with a real WIN/LOSS — building a genuine forward-tested
// record of "did Jarvis's own signals actually work", not a backtest.
const DEMO_TAG = 'AUTO-DEMO';

function alreadyLogged(tf, signal) {
  // Dedup: same TF + direction + entry (small tolerance) + still OPEN —
  // avoids re-logging the same live setup every time chat/LiveMonitor
  // re-checks while it's still the current bar's signal.
  const open = getTrades().filter(t => t.result === 'OPEN' && t.notes?.startsWith(DEMO_TAG));
  return open.some(t =>
    t.notes.includes(`TF:${tf}`) &&
    t.direction === signal.action &&
    Math.abs(t.entry - signal.entry) < signal.entry * 0.0005 // within 0.05%
  );
}

export function autoLogSignalAsTrade(tf, signal) {
  if (!signal || signal.action === 'WAIT' || !signal.ok) return null;
  if (alreadyLogged(tf, signal)) return null;

  return saveTrade({
    direction:   signal.action,
    setup_type:  'Structure + Fibonacci OTE + ADX (validated)',
    entry:       signal.entry,
    sl:          signal.sl,
    tp1:         signal.target,
    rr_planned:  signal.rr,
    jarvis_suggested: true,
    setup_grade: 'A', // all 5 gates passed by construction — a WAIT never reaches here
    notes: `${DEMO_TAG} | TF:${tf} | ${signal.reason}`,
    // Signal context for self-learning (win-rate by ADX/trend/HTF/TF) —
    // all of this was already computed by A_Signal.js, just never
    // persisted onto the trade record before now.
    timeframe:       tf,
    adx_at_signal:   signal.regime?.adx ?? null,
    trend_at_signal: signal.structure?.trend ?? null,
    htf_aligned:     signal.htfAligned ?? null,
  });
}

// A-Reversal's own auto-demo logger — SEPARATE from autoLogSignalAsTrade
// above (Sir's ask, 2026-08-23: "ani Jarvis ai madhe" — port the reversal
// concept into the app too, after Pine + Telegram relay). setup_type tags
// it distinctly so self-learning never conflates trend-following and
// counter-trend reversal trades into the same buckets. trend_at_signal is
// left null deliberately — "structure trend" doesn't map cleanly onto a
// trade that's explicitly AGAINST it; setup_type is what distinguishes
// these, not that field. Reuses alreadyLogged()'s dedup (TF+direction+
// entry+DEMO_TAG prefix) — generic enough for either signal source.
export function autoLogReversalAsTrade(tf, signal) {
  if (!signal || signal.action === 'WAIT' || !signal.ok || !signal.isReversal) return null;
  if (alreadyLogged(tf, signal)) return null;

  return saveTrade({
    direction:   signal.action,
    setup_type:  'Liquidity Sweep + RSI Divergence (4h reversal, validated)',
    entry:       signal.entry,
    sl:          signal.sl,
    tp1:         signal.target,
    rr_planned:  signal.rr,
    jarvis_suggested: true,
    setup_grade: 'A',
    notes: `${DEMO_TAG} | TF:${tf} | REVERSAL | ${signal.reason}`,
    timeframe:       tf,
    adx_at_signal:   signal.adx ?? null,
    trend_at_signal: null, // counter-trend by design — see comment above
    htf_aligned:     null,
  });
}

// Checks every OPEN auto-demo trade against the CURRENT price (via the
// same timeframe's live candles, checking high/low since entry — not just
// the latest close, so a wick that touched SL/target intrabar still
// counts, same discipline as A_Backtest.js's walk-forward).
export async function checkOpenDemoTrades() {
  const open = getTrades().filter(t => t.result === 'OPEN' && t.notes?.startsWith(DEMO_TAG));
  if (open.length === 0) return { checked: 0, closed: 0 };

  let closed = 0;
  for (const t of open) {
    const tfMatch = t.notes.match(/TF:(\w+)/);
    const tf = tfMatch ? tfMatch[1] : '1h';
    try {
      const { candles, ok } = await getLiveCandles(tf, '5d');
      if (!ok || !candles?.length) continue;

      // Only look at candles AFTER this trade's entry (by date), so an
      // old trade doesn't get closed by price action that happened before
      // it was even opened.
      const entryTime = new Date(t.date_logged).getTime();
      const since = candles.filter(c => new Date(c.date).getTime() >= entryTime);
      if (since.length === 0) continue;

      for (const c of since) {
        if (t.direction === 'BUY') {
          if (c.low <= t.sl)  { updateTradeResult(t.id, { result: 'LOSS', exit_price: t.sl,  jarvis_was_correct: false }); closed++; break; }
          if (c.high >= t.tp1) { updateTradeResult(t.id, { result: 'WIN',  exit_price: t.tp1, jarvis_was_correct: true  }); closed++; break; }
        } else {
          if (c.high >= t.sl) { updateTradeResult(t.id, { result: 'LOSS', exit_price: t.sl,  jarvis_was_correct: false }); closed++; break; }
          if (c.low <= t.tp1)  { updateTradeResult(t.id, { result: 'WIN',  exit_price: t.tp1, jarvis_was_correct: true  }); closed++; break; }
        }
      }
    } catch { /* one trade's check failing shouldn't block the rest */ }
  }
  return { checked: open.length, closed };
}
